import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ComponentType, ReactNode } from 'react';
import { Button, Drawer, Empty, Grid, Tooltip, Typography, theme, Layout } from 'antd';
import { DashboardOutlined, MenuFoldOutlined, MenuOutlined, MenuUnfoldOutlined, SearchOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';

import AdminLayout, { type AdminLayoutMobileMenuContext } from './AdminLayout.tsx';
import './ERPLayout.css';
import { useAppSelector } from '@store/store';
import { selectPlanCapabilities, selectSiteApps, selectSiteFeatureFlags } from '@store/slices/settingsSlice.tsx';
import {
  selectIsStaff,
  selectIsSuperuser,
  selectUserGroups,
  selectUserPermissions,
  selectUserRoles,
} from '@store/slices/userSlice.tsx';
import pageRoutes, { isErpPermission } from '@routes/pageRoutes.ts';
import {
  buildErpSections,
  GROUP_COPY,
  getRouteAccent,
  isErpRouteVisible,
  type ErpAccessContext,
} from '@erp/overview/erpSections.ts';
import { useErpAdvancedMode } from '@erp/overview/useErpAdvancedMode.ts';
import {
  DASHBOARD_NAV_ITEMS,
  findNavItemByPath,
  getVisibleNavItems,
} from '@features/dashboard/registry/dashboardNavRegistry.ts';
import type { DashboardNavItem } from '@features/dashboard/registry/types.ts';

const { Sider } = Layout;

type CSSVars = CSSProperties & Partial<Record<`--${string}`, string | number>>;

type ERPLayoutProps = {
  children?: ReactNode;
};

const OVERVIEW_ROUTE_KEY = 'erp.overview';
const ERP_HOME_PATH = '/erp';
const ERP_HOME_URL = '/erp';

const ERPLayout: React.FC<ERPLayoutProps> = ({ children }) => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  // MODOS:
  // - mobile: < md
  // - tablet: >= md && < xl
  // - desktop: >= xl
  const isXL = !!screens.xl;
  const isMD = !!screens.md;
  const mode: 'mobile' | 'tablet' | 'desktop' = isXL ? 'desktop' : isMD ? 'tablet' : 'mobile';

  const siteApps = useAppSelector(selectSiteApps) ?? [];
  const features = useAppSelector(selectSiteFeatureFlags) ?? {};
  const planCapabilities = useAppSelector(selectPlanCapabilities);
  const isStaff = useAppSelector(selectIsStaff);
  const isSuperuser = useAppSelector(selectIsSuperuser);
  const permissions = useAppSelector(selectUserPermissions) ?? [];
  const groups = useAppSelector(selectUserGroups);
  const roles = useAppSelector(selectUserRoles) ?? [];

  const permissionSet = useMemo(() => new Set(permissions), [permissions]);
  const hasErpPermission = useMemo(
    () => isSuperuser || permissions.some((permission) => isErpPermission(permission)),
    [permissions, isSuperuser],
  );

  const erpEnabled = useMemo(() => {
    const erpFeatureEnabled = typeof features.erp === 'boolean' ? features.erp : true;
    const crmFeatureEnabled = typeof features.crm === 'boolean' ? features.crm : true;
    return (erpFeatureEnabled && siteApps.includes('erp')) || (crmFeatureEnabled && siteApps.includes('crm'));
  }, [features.crm, features.erp, siteApps]);

  const accessContext = useMemo<ErpAccessContext>(
    () => ({
      erpEnabled,
      isStaff,
      isSuperuser,
      hasErpPermission,
      permissionSet,
      groups,
      siteApps,
      features,
      planCapabilities,
    }),
    [erpEnabled, features, groups, hasErpPermission, isStaff, isSuperuser, permissionSet, planCapabilities, siteApps],
  );

  const visibleNavItems = useMemo(
    () =>
      getVisibleNavItems(DASHBOARD_NAV_ITEMS, {
        apps: siteApps,
        features,
        isStaff,
        isSuperuser,
        permissions,
        groups,
        roles,
        planCapabilities,
      }),
    [features, groups, isStaff, isSuperuser, permissions, planCapabilities, roles, siteApps],
  );

  const navItemMap = useMemo(
    () => new Map<string, DashboardNavItem>(visibleNavItems.map((item) => [item.key, item])),
    [visibleNavItems],
  );

  const overviewRoute = useMemo(() => {
    const candidate = pageRoutes.find((route) => route.key === OVERVIEW_ROUTE_KEY);
    if (!candidate) return null;
    return isErpRouteVisible(candidate, accessContext) ? candidate : null;
  }, [accessContext]);

  // Estado visual:
  // desktop: colapsable normal
  // tablet: SIEMPRE colapsado; "expand" abre overlay
  // mobile: Drawer
  const [collapsed, setCollapsed] = useState(mode !== 'desktop'); // desktop inicia expanded, tablet/mobile no usan este valor
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [tabletOverlayOpen, setTabletOverlayOpen] = useState(false);

  const { advancedMode } = useErpAdvancedMode();

  const sections = useMemo(
    () =>
      buildErpSections({
        routes: pageRoutes,
        ...accessContext,
        advancedMode,
        groups,
      }),
    [accessContext, advancedMode, groups],
  );

  useEffect(() => {
    if (mode === 'desktop') {
      setCollapsed(false);
      setMobileNavOpen(false);
      setTabletOverlayOpen(false);
    } else if (mode === 'tablet') {
      setCollapsed(true); // siempre colapsado en el layout
      setMobileNavOpen(false);
      setTabletOverlayOpen(false);
    } else {
      // mobile
      setMobileNavOpen(false);
      setTabletOverlayOpen(false);
    }
  }, [mode]);

  const navigate = useNavigate();
  const location = useLocation();

  const activeNavItem = useMemo(
    () => findNavItemByPath(visibleNavItems, location.pathname, location.search),
    [location.pathname, location.search, visibleNavItems],
  );

  const handleNavigate = useCallback(
    (to: string) => {
      navigate(to);
      if (mode === 'mobile') setMobileNavOpen(false);
      if (mode === 'tablet') setTabletOverlayOpen(false);
    },
    [mode, navigate],
  );

  const handleToggleCollapse = useCallback(() => {
    if (mode === 'tablet') {
      // En tablet, "expand" abre/cierra overlay (no empuja layout)
      setTabletOverlayOpen((p) => !p);
      return;
    }
    if (mode === 'desktop') {
      setCollapsed((prev) => !prev);
    }
    // mobile no usa toggle
  }, [mode]);

  const handleRequestAccess = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.open('mailto:?subject=Solicitud%20de%20acceso%20ERP', '_blank');
  }, []);

  const handleOpenMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const handleCloseMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const handleOpenMobileSearch = useCallback((openCommandPalette: () => void) => {
    handleCloseMobileNav();

    if (typeof window === 'undefined') {
      openCommandPalette();
      return;
    }

    window.setTimeout(() => openCommandPalette(), 0);
  }, [handleCloseMobileNav]);

  const navSections = useMemo(() => sections.filter((section) => section.routes.length > 0), [sections]);

  const navIsCollapsed = mode === 'desktop' ? collapsed : mode === 'tablet' ? true : false;
  const hasSections = navSections.length > 0;

  const renderNavButton = (
    title: string,
    description: string | undefined,
    isDisabled: boolean,
    isActive: boolean,
    accent: string,
    onClick: () => void,
    Icon?: ComponentType,
  ) => {
    const itemStyles: CSSVars = {
      '--erp-nav-accent': accent,
    };

    const button = (
      <button
        type="button"
        onClick={onClick}
        className={[
          'erp-layout__nav-item',
          isActive ? 'erp-layout__nav-item--active' : '',
          isDisabled ? 'erp-layout__nav-item--disabled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={itemStyles}
        disabled={isDisabled}
        aria-current={isActive ? 'page' : undefined}
      >
        {Icon ? (
          <span className="erp-layout__nav-icon" aria-hidden="true">
            <Icon />
          </span>
        ) : null}
        <span className="erp-layout__nav-text">
          <span className="erp-layout__nav-label">{title}</span>
          {description ? <span className="erp-layout__nav-description">{description}</span> : null}
        </span>
      </button>
    );

    if (navIsCollapsed) {
      return (
        <Tooltip
          placement="right"
          title={
            <div className="erp-layout__tooltip-content">
              <strong>{title}</strong>
              {description ? <div>{description}</div> : null}
            </div>
          }
        >
          <span className="erp-layout__tooltip-wrapper">{button}</span>
        </Tooltip>
      );
    }
    return button;
  };

  const renderNavSections = () => {
    if (!hasSections) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div className="erp-layout__empty">
              <Typography.Text type="secondary">No hay módulos ERP disponibles para tu rol.</Typography.Text>
              <Button type="primary" size="small" onClick={handleRequestAccess}>
                Solicitar acceso
              </Button>
            </div>
          }
        />
      );
    }

    return (
      <nav className="erp-layout__nav" aria-label="Aplicaciones ERP">
        {navSections.map((section) => {
          const copy = GROUP_COPY[section.group];
          return (
            <div className="erp-layout__group" key={section.group} data-group={section.group}>
              <div className="erp-layout__group-label">{copy.title}</div>
              <div className="erp-layout__group-items" role="list">
                {section.routes.map((route) => {
                  const accent = getRouteAccent(route, token.colorPrimary);
                  const navItem = navItemMap.get(route.key);
                  const isActive = activeNavItem?.key === route.key;
                  const isDisabled = route.enabled === false || !navItem;
                  const Icon = route.icon;
                  const description = route.description ?? copy.description;

                  const handleClick = () => {
                    if (isDisabled) return;
                    handleNavigate(route.path);
                  };

                  return (
                    <div role="listitem" key={route.key} className="erp-layout__nav-item-wrapper">
                      {renderNavButton(route.title, description, isDisabled, isActive, accent, handleClick, Icon)}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
    );
  };

  const overviewNavItem = navItemMap.get(OVERVIEW_ROUTE_KEY);
  const overviewTitle = overviewRoute?.title ?? overviewNavItem?.title ?? 'Panel ERP';
  const overviewDescription =
    overviewRoute?.description ?? overviewNavItem?.description ?? GROUP_COPY.overview.description;
  const overviewAccent = overviewRoute
    ? getRouteAccent(overviewRoute, token.colorPrimary)
    : overviewNavItem
      ? `var(--ant-color-${overviewNavItem.colorToken}, ${token.colorPrimary})`
      : token.colorPrimary;
  const overviewIcon = (overviewRoute?.icon ?? overviewNavItem?.icon ?? DashboardOutlined) as ComponentType;
  const overviewIsActive =
    activeNavItem?.key === OVERVIEW_ROUTE_KEY || location.pathname.replace(/\/+$/, '') === ERP_HOME_PATH;
  const overviewIsDisabled = overviewRoute?.enabled === false;

  const handleOverviewClick = () => {
    if (overviewIsDisabled) return;
    handleNavigate(ERP_HOME_URL);
  };

  const overviewButton: ReactNode = (
    <div className="erp-layout__overview" key={overviewRoute?.key ?? 'erp:overview:fallback'}>
      {renderNavButton(
        overviewTitle,
        overviewDescription,
        Boolean(overviewIsDisabled),
        overviewIsActive,
        overviewAccent,
        handleOverviewClick,
        overviewIcon,
      )}
    </div>
  );

  const navHeader = (
    <div className="erp-layout__sidebar-header" style={mode === 'tablet' ? { position: 'relative' } : {}}>
      {!navIsCollapsed && mode === 'desktop' ? (
        <Typography.Title level={4} className="erp-layout__sidebar-title">
          Panel ERP
        </Typography.Title>
      ) : null}
      <Button
        type="text"
        size="small"
        icon={navIsCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        onClick={handleToggleCollapse}
        aria-label={navIsCollapsed ? 'Expandir menú ERP' : 'Contraer menú ERP'}
        className="erp-layout__toggle"
      />
    </div>
  );

  const navContent = (
    <div
      className="erp-layout__sidebar-scroll"
      style={mode === 'tablet' ? { position: 'relative', height: '100vh', paddingBottom: '5rem' } : { height: '100vh', paddingBottom: '5rem' }}
      role="presentation"
    >
      {mode !== 'mobile' ? navHeader : null}
      {overviewButton}
      {renderNavSections()}
    </div>
  );

  const sideBarNode =
    mode === 'desktop' || mode === 'tablet' ? (
      <Sider
        className="erp-layout__sidebar"
        data-collapsed={navIsCollapsed ? 'true' : 'false'}
        width={mode === 'desktop' && !navIsCollapsed ? 320 : 82}
        collapsedWidth={82}
        collapsed={navIsCollapsed}
        theme="light"
        style={{
          background: 'var(--surface-section)',
          position: 'sticky',
          // maxHeight: '99vh',
          maxHeight: '100vh',
          zIndex: 1,
        }}
      >
        {navContent}
      </Sider>
    ) : null;

  return (
    <AdminLayout
      breadcrumbOptions={{
        baseNavKey: OVERVIEW_ROUTE_KEY,
        baseFallback: { key: 'erp:overview:fallback', label: 'Panel ERP', href: ERP_HOME_URL, fromRegistry: false },
        groupFilter: 'erp',
      }}
      commandPaletteGroup="erp"
      searchButtonLabel="Buscar en ERP (Ctrl+K)"
      searchButtonAriaLabel="Buscar pantallas del ERP (Ctrl+K)"
      showCart={false}
      mobileHeaderMode="compact"
      sideBar={sideBarNode}
      sideMobileMenu={mode === 'mobile'
        ? ({ openCommandPalette, searchButtonLabel, searchButtonAriaLabel }: AdminLayoutMobileMenuContext) => (
            <>
              <Button
                type="text"
                size="large"
                icon={<MenuOutlined />}
                onClick={handleOpenMobileNav}
                className="erp-layout__mobile-trigger"
                aria-label="Abrir menu ERP"
              />
              <Drawer
                open={mobileNavOpen}
                onClose={handleCloseMobileNav}
                placement="left"
                size={320}
                title="Panel ERP"
                styles={{ body: { padding: 0 } }}
                className="erp-layout__drawer"
              >
                <div className="erp-layout__drawer-content">
                  <div className="erp-layout__drawer-tools">
                    <Button
                      type="default"
                      icon={<SearchOutlined />}
                      onClick={() => handleOpenMobileSearch(openCommandPalette)}
                      aria-label={searchButtonAriaLabel}
                      className="erp-layout__drawer-search-button"
                      block
                    >
                      {searchButtonLabel}
                    </Button>
                  </div>
                  {navContent}
                </div>
              </Drawer>
            </>
          )
        : null}
    >
      <div className="erp-layout">
        <main className="erp-layout__content">
          {mode === 'tablet' ? (
            <Drawer
              open={tabletOverlayOpen}
              onClose={() => setTabletOverlayOpen(false)}
              placement="left"
              size={320}
              title="Panel ERP"
              styles={{ body: { padding: 0 } }}
              className="erp-layout__drawer"
            >
              <div className="erp-layout__drawer-content">{navContent}</div>
            </Drawer>
          ) : null}

          {children}
        </main>
      </div>
    </AdminLayout>
  );
};

export default ERPLayout;
